/**
 * Encapsulates the state and CRUD operations for a single client-supplemental
 * record. Instantiate in a Vue 2 component's `data()` — Vue makes the
 * instance properties reactive automatically.
 *
 * Usage:
 *   data() {
 *     return {
 *       myField: new SupplementalField({
 *         key: "my_supplemental_key",
 *         label: "My Field",
 *         softDeleteOnEmpty: true,
 *       }),
 *     };
 *   }
 */
class SupplementalField {
  /**
   * @param {Object} options
   * @param {string} options.key - The client-supplemental key.
   * @param {string} options.label - Human-readable label for success/error messages.
   * @param {boolean} [options.softDeleteOnEmpty=false] - When true, persisting an
   *   empty value patches `{ status: "Deleted" }` instead of saving an empty string.
   * @param {string} [options.defaultValue=""] - Initial/reset value.
   */
  constructor({ key, label, softDeleteOnEmpty = false, defaultValue = "" }) {
    this.key = key;
    this.label = label;
    this.softDeleteOnEmpty = softDeleteOnEmpty;
    this.defaultValue = defaultValue;

    this.value = defaultValue;
    this.supplementalId = null;
    this.loading = false;
    this.saving = false;
  }

  /**
   * Load the field value from the API.
   *
   * When used inside a Vue 2 component with watchers on `this.value`, pass
   * `deferFn` (wrapping `vm.$nextTick`) so the loading flag is reset *after*
   * the queued watcher flush — preventing the watcher from treating the
   * programmatic update as a user edit.
   *
   * @param {number|string} clientId
   * @param {Object} services
   * @param {Function} services.getClientSupplemental
   * @param {Function} services.showError - dispatch("showError", { message })
   * @param {Function} [services.deferFn] - Optional deferred callback (e.g. Vue $nextTick wrapper).
   */
  async load(clientId, { getClientSupplemental, showError, deferFn }) {
    if (!clientId) return;
    try {
      this.loading = true;
      const { value, clientSupplementalId } = await getClientSupplemental({
        clientId,
        key: this.key,
        errorContext: `Failed to load saved ${this.label}`,
      });
      this.supplementalId = clientSupplementalId;
      this.value = value || this.defaultValue;
    } catch (error) {
      console.error(`Failed to load ${this.label}`, error);
      showError({ message: `Failed to load saved ${this.label}. Please try again later.` });
    } finally {
      const resetLoading = () => {
        this.loading = false;
      };
      if (deferFn) {
        deferFn(resetLoading);
      } else {
        resetLoading();
      }
    }
  }

  /**
   * Persist the current value to the API. Handles create, update, and
   * soft-delete (when `softDeleteOnEmpty` is configured).
   *
   * @param {number|string} clientId
   * @param {*} value - The value to persist (will be coerced to string by the service).
   * @param {Object} services
   * @param {Function} services.saveClientSupplemental
   * @param {Function} services.softDeleteClientSupplemental
   * @param {Function} services.showSuccess
   * @param {Function} services.showError
   */
  async persist(
    clientId,
    value,
    { saveClientSupplemental, softDeleteClientSupplemental, showSuccess, showError }
  ) {
    if (!clientId) return;
    const trimmed = value === null || value === undefined ? "" : String(value).trim();
    const isDeleting = this.softDeleteOnEmpty && !trimmed && this.supplementalId;

    try {
      this.saving = true;
      if (isDeleting) {
        await softDeleteClientSupplemental({
          clientId,
          key: this.key,
          errorContext: `Failed to delete ${this.label}`,
        });
        this.supplementalId = null;
        showSuccess({ message: `${this.label} removed.` });
      } else if (!this.softDeleteOnEmpty || trimmed) {
        const persistValue = this.softDeleteOnEmpty ? trimmed : value;
        const { clientSupplementalId } = await saveClientSupplemental({
          clientId,
          key: this.key,
          value: persistValue,
          clientSupplementalId: this.supplementalId,
          updateErrorContext: `Failed to update ${this.label}`,
          createErrorContext: `Failed to save ${this.label}`,
        });
        this.supplementalId = clientSupplementalId;
        showSuccess({ message: `${this.label} saved.` });
      }
    } catch (error) {
      console.error(`Failed to save ${this.label}`, error);
      showError({ message: `Failed to save ${this.label}. Please try again later.` });
    } finally {
      this.saving = false;
    }
  }

  /**
   * Reset the field to its default value and persist the reset.
   * If `softDeleteOnEmpty` is true, soft-deletes the record.
   * Otherwise saves the default/empty value.
   *
   * @param {number|string} clientId
   * @param {Object} services
   * @param {Function} services.saveClientSupplemental
   * @param {Function} services.softDeleteClientSupplemental
   */
  async reset(clientId, { saveClientSupplemental, softDeleteClientSupplemental }) {
    this.value = this.defaultValue;
    if (!clientId) return;

    if (this.softDeleteOnEmpty) {
      if (this.supplementalId) {
        await softDeleteClientSupplemental({
          clientId,
          key: this.key,
          errorContext: `Failed to reset ${this.label}`,
        });
        this.supplementalId = null;
      }
    } else {
      const { clientSupplementalId } = await saveClientSupplemental({
        clientId,
        key: this.key,
        value: this.defaultValue,
        clientSupplementalId: this.supplementalId,
        updateErrorContext: `Failed to reset ${this.label}`,
        createErrorContext: `Failed to reset ${this.label}`,
      });
      this.supplementalId = clientSupplementalId;
    }
  }
}

export default SupplementalField;
